// src/pages/appointment/InformedConsentForm.js
// Formulario de consentimiento informado: genera PDF, sube a Firebase y envía al backend

import React, { useState } from 'react';
import { Form, Row, Col, Card, Button, Spinner, Alert } from 'react-bootstrap';
import { storage } from '../../firebase/firebaseConfig';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import jsPDF from 'jspdf';
import { createInformedConsent, updateInformedConsent, getInformedConsentByAppointment } from '../../services/informedConsentApi';
import { getAppointmentDetails } from '../../services/appointmentDetailApi';
import { getGroomingItems } from '../../services/appointmentApi';
import AlertModal from '../../components/AlertModal';
import useAlertModal from '../../hooks/useAlertModal';
import { useBranding } from '../../context/BrandingContext';

import defaultLogo from '../../assets/img/Logo.jpg';

const PHYSICAL_OPTIONS = ['Óptimo', 'Cansado', 'Problemas de piel', 'Otro'];
const BEHAVIOR_OPTIONS = ['Tranquilo', 'Nervioso', 'Agresivo con otros perros', 'Miedoso', 'Otro'];

// Paleta minimalista pet-friendly (RGB 0–255)
const COLORS = {
  teal: [42, 157, 143],       // #2A9D8F
  coral: [231, 111, 81],     // #E76F51
  salmon: [244, 162, 97],    // #F4A261
  dark: [38, 70, 83],        // #264653
  gray: [107, 114, 128],     // #6B7280
  lightGray: [229, 231, 235], // #E5E7EB
  white: [255, 255, 255]
};

const loadImageAsBase64 = (url) =>
  fetch(url)
    .then((r) => r.blob())
    .then(
      (blob) =>
        new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onloadend = () => resolve(r.result);
          r.onerror = reject;
          r.readAsDataURL(blob);
        })
    );

/**
 * Genera el PDF del consentimiento con estilo moderno, pet y minimalista
 * itemsMap: { [id_item]: { description, name, price } } para resolver el nombre real del servicio
 * branding: { companyName, tagline }
 */
const buildConsentPdf = (appointment, formValues, services = [], itemsMap = {}, logoBase64 = null, branding = {}) => {
  const companyName = branding.companyName || 'DotoniWoof';
  const tagline = branding.tagline || 'Cuidamos a tu mascota con cariño';
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const w = 210;
  const margin = 18;
  const contentW = w - margin * 2;
  const maxPageHeight = 297 - 8; // Altura de página menos pie
  let y = 0;

  // —— Encabezado con logo Dotoni ——
  doc.setFillColor(...COLORS.teal);
  doc.rect(0, 0, w, 32, 'F');

  if (logoBase64) {
    try {
      doc.addImage(logoBase64, 'JPEG', margin, 6, 22, 20);
    } catch (_) {
      // ignorar si el formato falla
    }
  }

  doc.setTextColor(...COLORS.white);
  doc.setFontSize(18);
  doc.setFont(undefined, 'bold');
  doc.text(companyName, logoBase64 ? margin + 26 : margin, 14);
  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  doc.text('Consentimiento informado · Servicios de grooming', logoBase64 ? margin + 26 : margin, 21);
  doc.text(tagline, logoBase64 ? margin + 26 : margin, 26);

  y = 38;

  // —— Información de la cita ——
  const infoPadding = 6;
  const infoLineH = 6;
  const infoTitleH = 8;
  let infoLines = 0;
  if (appointment?.start_at) infoLines += 1;
  if (appointment?.pet_name) infoLines += 1;
  if (appointment?.client_name) infoLines += 1;
  if (appointment?.duration_min) infoLines += 1;
  const infoBoxH = infoPadding + infoTitleH + infoLineH + (infoLines * infoLineH) + infoPadding;
  
  doc.setFillColor(240, 252, 250); // fondo teal muy suave
  if (typeof doc.roundedRect === 'function') {
    doc.roundedRect(margin, y, contentW, infoBoxH, 2, 2, 'F');
  } else {
    doc.rect(margin, y, contentW, infoBoxH, 'F');
  }
  
  doc.setTextColor(...COLORS.teal);
  doc.setFontSize(10);
  doc.setFont(undefined, 'bold');
  doc.text('Información de la Cita', margin + infoPadding, y + infoPadding + 5);
  
  doc.setTextColor(...COLORS.dark);
  doc.setFontSize(9);
  doc.setFont(undefined, 'normal');
  let infoY = y + infoPadding + infoTitleH + infoLineH + 4;
  
  if (appointment?.start_at) {
    const date = new Date(appointment.start_at);
    const dateStr = date.toLocaleDateString('es-ES', { dateStyle: 'long' });
    const timeStr = date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    doc.text(`Fecha y hora: ${dateStr} a las ${timeStr}`, margin + infoPadding, infoY);
    infoY += infoLineH;
  }
  
  if (appointment?.pet_name) {
    doc.text(`Mascota: ${appointment.pet_name}`, margin + infoPadding, infoY);
    infoY += infoLineH;
  }
  
  if (appointment?.client_name) {
    doc.text(`Responsable: ${appointment.client_name}`, margin + infoPadding, infoY);
    infoY += infoLineH;
  }
  
  if (appointment?.duration_min) {
    doc.text(`Duración: ${appointment.duration_min} minutos`, margin + infoPadding, infoY);
  }
  
  y += infoBoxH + 6;

  // Línea separadora
  doc.setDrawColor(...COLORS.lightGray);
  doc.setLineWidth(0.4);
  doc.line(margin, y, w - margin, y);
  y += 8;

  // —— Diseño a dos columnas ——
  const colGap = 6; // espacio entre columnas
  const colW = (contentW - colGap) / 2;
  const colLeftX = margin;
  const colRightX = margin + colW + colGap;
  const padding = 5;
  const lineHeight = 5;
  const labelAreaH = 5;
  const gapLabelValue = 4;
  const startY = y;
  
  // Función para dibujar una sección en una columna específica
  const sectionCol = (x, yPos, label, value, multiline = false, maxHeight = 0) => {
    const textWidth = colW - padding * 2;
    const valueStr = (value || '—').toString();
    const lines = doc.splitTextToSize(valueStr, textWidth);
    
    const valueHeight = lines.length * lineHeight;
    const boxH = padding + labelAreaH + gapLabelValue + valueHeight + padding;
    
    doc.setFillColor(255, 248, 240); // cremita muy suave
    if (typeof doc.roundedRect === 'function') {
      doc.roundedRect(x, yPos, colW, boxH, 2, 2, 'F');
    } else {
      doc.rect(x, yPos, colW, boxH, 'F');
    }
    
    doc.setTextColor(...COLORS.teal);
    doc.setFontSize(8);
    doc.setFont(undefined, 'bold');
    doc.text(label, x + padding, yPos + padding + 3);
    
    let valueY = yPos + padding + labelAreaH + gapLabelValue + 3;
    doc.setTextColor(...COLORS.dark);
    doc.setFont(undefined, 'normal');
    doc.setFontSize(8);
    for (let i = 0; i < lines.length; i++) {
      doc.text(lines[i], x + padding, valueY);
      valueY += lineHeight;
    }
    
    return boxH;
  };

  // Columna izquierda
  let leftY = startY;
  const h1 = sectionCol(colLeftX, leftY, 'Estado físico', formValues.physical_condition);
  leftY += h1 + 4;
  const h2 = sectionCol(colLeftX, leftY, 'Comportamiento', formValues.behavior_notes);
  leftY += h2 + 4;
  
  // Columna derecha
  let rightY = startY;
  const h3 = sectionCol(colRightX, rightY, 'Última vacunación', formValues.last_vaccination_date || 'No indicada');
  rightY += h3 + 4;
  const h4 = sectionCol(colRightX, rightY, 'Notas adicionales', formValues.additional_notes || 'Ninguna', true);
  rightY += h4 + 4;
  
  // y se actualiza con la columna más alta
  y = Math.max(leftY, rightY) + 4;

  // —— Servicios realizados (compacto, ancho completo) ——
  if (services && services.length > 0) {
    // Verificar espacio disponible antes de dibujar
    const minSpaceNeeded = 25;
    if (y > maxPageHeight - minSpaceNeeded - 8) {
      // Si no hay espacio, ajustar y hacia arriba un poco
      y = Math.min(y, maxPageHeight - minSpaceNeeded - 8);
    }
    
    doc.setFillColor(240, 252, 250);
    const servicesHeaderH = 6;
    const rowH = 5;
    const maxRows = Math.min(services.length, 4); // máximo 4 filas visibles
    const servicesTableH = servicesHeaderH + (maxRows * rowH) + 8;
    
    if (typeof doc.roundedRect === 'function') {
      doc.roundedRect(margin, y, contentW, servicesTableH, 2, 2, 'F');
    } else {
      doc.rect(margin, y, contentW, servicesTableH, 'F');
    }
    
    doc.setTextColor(...COLORS.teal);
    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    doc.text('Servicios Realizados', margin + 4, y + 4);
    
    // Encabezados de tabla (más compactos)
    doc.setFontSize(7);
    doc.setFont(undefined, 'bold');
    doc.text('Servicio', margin + 4, y + 9);
    doc.text('Cant.', margin + 95, y + 9);
    doc.text('Precio', margin + 110, y + 9);
    doc.text('Subtotal', margin + 150, y + 9);
    
    doc.setFont(undefined, 'normal');
    doc.setTextColor(...COLORS.dark);
    doc.setFontSize(7);
    let serviceY = y + 13;
    let total = 0;
    
    services.slice(0, maxRows).forEach((service) => {
      const item = itemsMap[service.id_item] || {};
      const description = (service.description || item.description || item.name || 'Servicio').toString();
      const descLines = doc.splitTextToSize(description, 85);
      const quantity = service.quantity || 1;
      const unitPrice = parseFloat(service.unit_price || 0);
      const subtotal = quantity * unitPrice;
      total += subtotal;
      
      // Primera línea del servicio
      doc.text(descLines[0] || description.substring(0, 20), margin + 4, serviceY);
      doc.text(quantity.toString(), margin + 95, serviceY);
      doc.text(`$${unitPrice.toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`, margin + 110, serviceY);
      doc.text(`$${subtotal.toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`, margin + 150, serviceY);
      
      serviceY += rowH;
    });
    
    // Si hay más servicios, mostrar total acumulado
    if (services.length > maxRows) {
      services.slice(maxRows).forEach((service) => {
        const quantity = service.quantity || 1;
        const unitPrice = parseFloat(service.unit_price || 0);
        total += quantity * unitPrice;
      });
    }
    
    // Total
    doc.setDrawColor(...COLORS.teal);
    doc.setLineWidth(0.3);
    doc.line(margin + 4, serviceY, margin + contentW - 4, serviceY);
    serviceY += 3;
    doc.setFont(undefined, 'bold');
    doc.setFontSize(8);
    doc.text('Total:', margin + 130, serviceY);
    doc.text(`$${total.toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`, margin + 150, serviceY);
    
    y += servicesTableH + 2;
  }

  // —— Aviso legal (compacto) ——
  if (y > maxPageHeight - 20) {
    y = maxPageHeight - 20; // Ajustar posición si está muy abajo
  }
  
  doc.setDrawColor(...COLORS.salmon);
  doc.setLineWidth(0.4);
  doc.line(margin, y, margin + 25, y);
  doc.setTextColor(...COLORS.dark);
  doc.setFontSize(8);
  const legalText = 'El responsable declara haber sido informado sobre el procedimiento y autoriza la realización de los servicios de grooming indicados.';
  const legalLines = doc.splitTextToSize(legalText, contentW);
  doc.text(legalLines, margin, y + 4);
  y += 4 + (legalLines.length * 4);
  doc.setTextColor(...COLORS.gray);
  doc.setFontSize(7);
  doc.text(`Firma / Registro digital · Documento generado por ${companyName}`, margin, y);

  // —— Pie con toque coral ——
  doc.setFillColor(...COLORS.coral);
  doc.rect(0, 297 - 8, w, 8, 'F');
  doc.setTextColor(...COLORS.white);
  doc.setFontSize(8);
  doc.text(`${companyName} · Consentimiento informado`, margin, 293);

  return doc;
};

/**
 * Sube el PDF (Blob) a Firebase Storage y devuelve la URL
 */
const uploadPdfToFirebase = (blob, appointmentId) => {
  return new Promise((resolve, reject) => {
    const fileName = `informed_consent/appointment_${appointmentId}_${Date.now()}.pdf`;
    const storageRef = ref(storage, fileName);
    const metadata = { contentType: 'application/pdf' };
    const uploadTask = uploadBytesResumable(storageRef, blob, metadata);

    uploadTask.on(
      'state_changed',
      () => {},
      (err) => reject(err),
      () => {
        getDownloadURL(uploadTask.snapshot.ref).then(resolve).catch(reject);
      }
    );
  });
};

const InformedConsentForm = ({ appointment, onSuccess, onCancel }) => {
  const branding = useBranding();
  const [formData, setFormData] = useState({
    physical_condition: '',
    behavior_notes: '',
    last_vaccination_date: '',
    additional_notes: ''
  });
  const [loading, setLoading] = useState(false);
  const { showAlert, showAlertModal, alertMessage, alertType, hideAlert } = useAlertModal();
  const [existingConsent, setExistingConsent] = useState(null);

  React.useEffect(() => {
    if (appointment?.id_appointment) {
      getInformedConsentByAppointment(appointment.id_appointment).then(({ ok, data }) => {
        const consent = data?.data ?? data?.message;
        if (ok && consent && (Array.isArray(consent) ? consent[0] : consent)) {
          const c = Array.isArray(consent) ? consent[0] : consent;
          setExistingConsent(c);
          setFormData({
            physical_condition: c.physical_condition || '',
            behavior_notes: c.behavior_notes || '',
            last_vaccination_date: c.last_vaccination_date ? c.last_vaccination_date.slice(0, 10) : '',
            additional_notes: c.additional_notes || ''
          });
        }
      });
    }
  }, [appointment?.id_appointment]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!appointment?.id_appointment) {
      showAlert('No hay cita asociada.', 'error');
      return;
    }

    setLoading(true);

    try {
      const logoUrlToLoad = branding.logoUrl && branding.logoUrl.startsWith('http') ? branding.logoUrl : (branding.logoUrl ? `${window.location.origin}${branding.logoUrl}` : defaultLogo);
      const logoBase64 = await loadImageAsBase64(logoUrlToLoad).catch(() => loadImageAsBase64(defaultLogo).catch(() => null));
      const brandingInfo = { companyName: branding.companyName, tagline: branding.tagline };
      
      // Cargar servicios de la cita y catálogo de ítems para resolver nombres
      let services = [];
      let itemsMap = {};
      try {
        const [detailsRes, itemsRes] = await Promise.all([
          getAppointmentDetails(appointment.id_appointment),
          getGroomingItems()
        ]);
        if (detailsRes?.ok && detailsRes?.data?.code === 200) {
          const responseData = detailsRes.data.data || detailsRes.data.message || [];
          services = Array.isArray(responseData) ? responseData : [];
        }
        if (itemsRes?.ok && itemsRes?.data?.code === 200) {
          const items = itemsRes.data.data || itemsRes.data.message || [];
          const list = Array.isArray(items) ? items : [];
          list.forEach((it) => {
            if (it.id_item) itemsMap[it.id_item] = it;
          });
        }
      } catch (err) {
        console.warn('Error cargando servicios o ítems:', err);
      }
      
      const doc = buildConsentPdf(appointment, formData, services, itemsMap, logoBase64, brandingInfo);
      const blob = doc.output('blob');

      const urlPdf = await uploadPdfToFirebase(blob, appointment.id_appointment);

      const body = {
        url_pdf_firebase: urlPdf,
        physical_condition: formData.physical_condition || null,
        behavior_notes: formData.behavior_notes || null,
        last_vaccination_date: formData.last_vaccination_date || null,
        additional_notes: formData.additional_notes || null,
        signed_by_person: 1
      };

      let result;
      if (existingConsent?.id_informed_consent) {
        result = await updateInformedConsent(existingConsent.id_informed_consent, body);
      } else {
        result = await createInformedConsent({
          id_appointment: appointment.id_appointment,
          ...body
        });
      }

      const { ok, status, data } = result;
      if (ok && (status === 200 || status === 201 || data?.code === 200 || data?.code === 201)) {
        showAlert('Consentimiento informado guardado exitosamente', 'success');
        onSuccess?.({ ...data, url_pdf_firebase: urlPdf });
      } else {
        showAlert(data?.message || 'Error al guardar el consentimiento.', 'error');
      }
    } catch (err) {
      console.error('Error consentimiento informado:', err);
      showAlert(err?.message || 'Error al generar PDF o subir a Firebase.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Card className="mb-3">
        <Card.Header className="bg-light">
          <strong>Consentimiento informado</strong>
          {appointment?.pet_name && (
            <span className="ms-2 text-muted">
              — {appointment.pet_name}
              {appointment.client_name && ` · ${appointment.client_name}`}
            </span>
          )}
        </Card.Header>
        <Card.Body>
          {existingConsent?.url_pdf_firebase && (
            <Alert variant="info" className="mb-3">
              Ya existe un consentimiento para esta cita.{' '}
              <a href={existingConsent.url_pdf_firebase} target="_blank" rel="noopener noreferrer">
                Ver PDF
              </a>
            </Alert>
          )}

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Estado físico *</Form.Label>
                <Form.Select
                  name="physical_condition"
                  value={formData.physical_condition}
                  onChange={handleChange}
                  required
                >
                  <option value="">Seleccione...</option>
                  {PHYSICAL_OPTIONS.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Comportamiento *</Form.Label>
                <Form.Select
                  name="behavior_notes"
                  value={formData.behavior_notes}
                  onChange={handleChange}
                  required
                >
                  <option value="">Seleccione...</option>
                  {BEHAVIOR_OPTIONS.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Fecha de última vacunación</Form.Label>
            <Form.Control
              type="date"
              name="last_vaccination_date"
              value={formData.last_vaccination_date}
              onChange={handleChange}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Notas adicionales (dueño o groomer)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="additional_notes"
              value={formData.additional_notes}
              onChange={handleChange}
              placeholder="Observaciones, alergias, precauciones..."
            />
          </Form.Group>
        </Card.Body>
      </Card>

      <div className="d-flex justify-content-end gap-2">
        {onCancel && (
          <Button variant="outline-secondary" onClick={onCancel} disabled={loading}>
            Cancelar
          </Button>
        )}
        <Button type="submit" variant="primary" disabled={loading}>
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Generando PDF y guardando...
            </>
          ) : (
            'Generar PDF y Guardar Consentimiento'
          )}
        </Button>
      </div>
      {/* Modal de Alerta Temporal */}
      <AlertModal
        show={showAlertModal}
        onHide={hideAlert}
        message={alertMessage}
        type={alertType}
      />
    </Form>
  );
};

export default InformedConsentForm;
